import { CancelToken, isCancel } from 'axios'

const getDefaultState = () => {
  return {
    // All eligibilities for the current opportunity
    all: [],
    // The eligibilities currently visible
    active: [],
    // What filters are currently applied
    filters: {
      name: '',
      institutions: [],
      categories: [],
      types: [],
      disciplines: [],
      grade: [],
      ethnicities: [],
      genders: [],
    },
    // Used to indicate when waiting on the server
    loading: false,
    // The current page being viewed
    page: 1,
    // Size of current page being viewed
    rowsPerPage: 25,
    // The search string being applied
    search: '',
    // Selected candidates used for bulk actions
    selected: [],
    // Sorting
    sort: {
      by: 'profile.updatedAt',
      descending: true,
    },
    // Token source used to abandon requests
    source: null,
    // The total records matching the filter according to the server (for pagination)
    total: 0,
    // Used to indicate progress when loading
    progress: null,
  }
}

const state = getDefaultState()

const getters = {
  active: state => state.active,
  find: state => id => state.all.find(eligibility => eligibility.id == id),
  hasIncomplete: state => state.active.some(eligibility => !eligibility.complete),
  loading: state => state.loading,
  selected: state => state.selected,
  filters: state => state.filters,
  progress: state => {
    if (state.progress == null) {
      return 0
    } else {
      return (state.progress[0] * 100.0) / state.progress[2]
    }
  },
}

const mutations = {
  setActive(
    state,
    {
      items = [],
      page = 1,
      per = 25,
      total = 0,
      search = null,
      sort = { by: 'profile.updatedAt', descending: true },
      filters = {},
    }
  ) {
    state.search = search
    state.active = items
    state.sort = sort
    state.filters = { ...filters, status: state.filters.status }
    state.page = page
    state.rowsPerPage = per
    state.total = total
    state.loading = false
    state.select = []
  },

  set(state, eligibility) {
    const find = state.all.find(o => o.id == eligibility.id)
    if (find) state.all.splice(state.all.indexOf(find), 1, eligibility)
    if (!find) state.all.push(eligibility)
  },

  resetState(state) {
    Object.assign(state, getDefaultState())
  },

  remove(state, id) {
    state.active = state.active.filter(o => o.id != id)
  },

  select(state, value) {
    if (Array.isArray(value)) {
      for (let id of value) {
        if (state.selected.indexOf(id) == -1) state.selected.push(id)
      }
    } else state.selected.push(value)
  },

  deselect(state, value) {
    if (Array.isArray(value)) {
      state.selected = state.selected.filter(s => value.indexOf(s) == -1)
    } else state.selected = state.selected.filter(s => s !== value)
  },

  clearSelected(state) {
    state.selected = []
  },
  append(state, { counts, eligibilities }) {
    for (let eligibility of eligibilities) {
      const find = state.all.find(o => o.id === eligibility.id)
      if (find)
        state.all.splice(state.all.indexOf(find), 1, {
          ...eligibility,
          $type: 'OpportunityApplication',
        })
      if (!find) state.all.push({ ...eligibility, $type: 'Eligibility' })
      if (counts[eligibility.status]) {
        counts[eligibility.status]++
      } else {
        counts[eligibility.status] = 1
      }
    }
  },
  setProgress(state, total = 0) {
    if (state.all.length == total) {
      state.progress = null // done
    } else {
      state.progress = [state.all.length, total]
    }
  },
}

const actions = {
  load({ commit, state }, { opportunity = Vue.$route.params.id } = {}) {
    this.$authorize('list', 'Eligibility')
    // Cancel previous load actions
    if (state.source) state.source.cancel()
    state.source = CancelToken.source()

    const pageSize = 250

    state.loading = true
    return Vue.$http
      .get('admin/eligibilities', {
        params: { opportunity, sort: 'id ASC', page: 1, per: pageSize },
        cancelToken: state.source.token,
      })
      .then(
        async response => {
          if (response.data.items) {
            const total = response.data.total
            var counts = {}
            // Set the total
            state.total = response.data.total
            // Add the eligibilities
            const eligibilities = response.data.items
            commit('append', { counts, eligibilities })
            commit('setProgress', total)
            // Update the phase counts
            commit('admin/opportunities/setCounts', counts, { root: true })
            // Load the remainder synchronously
            if (state.total > pageSize) {
              const pages = Math.ceil(state.total / pageSize)
              for (let page = 2; page <= pages; page++) {
                state.source = CancelToken.source()
                let pageResponse = await Vue.$http.get('admin/eligibilities', {
                  params: { opportunity, sort: 'id ASC', page: page, per: pageSize },
                  cancelToken: state.source.token,
                })
                if (pageResponse.data.items) {
                  commit('append', { counts, eligibilities: pageResponse.data.items })
                  commit('setProgress', total)
                  // Update the phase counts
                  commit('admin/opportunities/setCounts', counts, { root: true })
                }
              }
            }
            // Update the phase counts
            commit('admin/opportunities/setCounts', counts, { root: true })
          }
          state.loading = false
        },
        errors => {
          if (!isCancel(errors)) state.loading = false
        }
      )
  },
  abandon({ state }, reason) {
    if (state.source) {
      state.source.cancel(reason)
      state.source = null
      state.loading = false
    }
  },

  resetState({ commit }) {
    commit('resetState')
  },

  search(
    { commit, state },
    {
      applied = false,
      descending,
      eligible = true,
      filters,
      opportunity = Vue.$route.params.id,
      page,
      sortBy,
      rowsPerPage,
    } = {}
  ) {
    this.$authorize('list', 'Eligibility')

    // Cancel previous search actions
    if (state.source) {
      state.source.cancel()
      state.loading = false
    }
    state.source = CancelToken.source()
    descending = typeof descending === 'boolean' ? descending : state.sort.descending
    state.filters = filters || state.filters
    page = page || state.page
    const per = rowsPerPage || state.rowsPerPage
    const search = filters && filters.search ? filters.search : state.search
    sortBy = sortBy || state.sort.by
    // Candidate filters sets sortBy to completion only
    sortBy = sortBy.toLowerCase() === 'completion' ? 'profile.completion' : sortBy
    const sort = `${sortBy} ${descending ? 'DESC' : 'ASC'}`

    state.loading = true
    return Vue.$http
      .get('admin/eligibilities', {
        params: { ...state.filters, opportunity, page, search, sort, per, applied, eligible },
        cancelToken: state.source.token,
      })
      .then(
        response => commit('setActive', response.data),
        errors => {
          if (!isCancel(errors)) state.loading = false
        }
      )
  },

  updateFilters({ commit, state }, assigned) {
    this.$authorize('list', 'Eligibility')

    Object.assign(state.filters, { ...assigned })
    let { filters } = state

    // Remove complete and referred filter if all applications are required
    if (filters['complete'] == false) delete state.filters['complete']
    if (filters['referred'] == false) delete state.filters['referred']
    // Set pagination
    if (assigned.page) state.page = assigned.page
    if (assigned.rowsPerPage) state.rowsPerPage = assigned.rowsPerPage
    // Set counts
    var counts = {
      Qualifying: 0,
      Invited: 0,
    }
    // Apply filters from `all` to `active`
    state.active = applyFilters(state.all, filters, counts)
    // Set the total
    state.total = state.active.length
    // Update the phase counts
    commit('admin/opportunities/setCounts', counts, { root: true })
    // Sort results
    applySort(state.active, state.sort)
    // Paginate
    if (state.total < (state.page - 1) * state.rowsPerPage + 1) state.page = 1
    state.active.splice(0, (state.page - 1) * state.rowsPerPage) // drop
    state.active.splice(state.rowsPerPage, state.active.length - state.rowsPerPage) // take
  },

  updateSort({ dispatch, state }, sort) {
    state.sort = sort
    dispatch('updateFilters', { page: 1 })
  },

  fetch({ commit, getters }, id) {
    this.$authorize('read', 'Eligibility')

    const eligibility = getters.find(id)

    if (eligibility) {
      commit('set', Object.assign(eligibility, { loading: true }))
    } else {
      commit('set', { id, loading: true })
    }

    return Vue.$http
      .get(`admin/opportunities/${Vue.$route.params.id}/eligibilities/${id}`)
      .then(response => {
        commit('set', Object.assign(response.data, { loading: false }))
        return response
      })
  },

  invite({ commit, dispatch }, eligibilityIds) {
    this.$authorize('create', 'Invite')

    if (Array.isArray(eligibilityIds)) {
      return Vue.$http.post(`admin/eligibilities/invite`, { eligibilityIds }).then(async () => {
        for (let id of eligibilityIds) {
          await dispatch('fetch', id)
        }
        commit('deselect', eligibilityIds)
        dispatch('updateFilters', {})
      })
    }
    const id = parseInt(eligibilityIds)
    return Vue.$http
      .patch(`admin/opportunities/${Vue.$route.params.id}/eligibilities/${id}/invite`)
      .then(() => dispatch('fetch', id))
      .then(() => commit('deselect', id))
      .then(() => dispatch('updateFilters', {}))
  },
}

function applySort(active, sort) {
  // Sort by ID
  if (sort.by == 'id' && sort.descending == false) active.sort((a, b) => (a.id > b.id ? 1 : -1))
  if (sort.by == 'id' && sort.descending == true) active.sort((a, b) => (a.id > b.id ? -1 : 1))

  // Completion
  if (sort.by == 'completion' && sort.descending == false)
    active.sort((a, b) => (a.completion > b.completion ? 1 : -1))
  if (sort.by == 'completion' && sort.descending == true)
    active.sort((a, b) => (a.completion > b.completion ? -1 : 1))

  // Name
  if (sort.by == 'profile.firstNames.sort' && sort.descending == false)
    active.sort((a, b) => (a.profile.fullName > b.profile.fullName ? 1 : -1))
  if (sort.by == 'profile.firstNames.sort' && sort.descending == true)
    active.sort((a, b) => (a.profile.fullName > b.profile.fullName ? -1 : 1))

  // Sort by profile update
  if (sort.by == 'profile.updatedAt' && sort.descending == false)
    active.sort((a, b) => (new Date(a.profile.updatedAt) > new Date(b.profile.updatedAt) ? 1 : -1))
  if (sort.by == 'profile.updatedAt' && sort.descending == true)
    active.sort((a, b) => (new Date(a.profile.updatedAt) > new Date(b.profile.updatedAt) ? -1 : 1))

  // Incomplete always below complete
  active.sort((a, b) => (a.complete === b.complete ? 0 : b.complete ? 1 : -1))
}

// This takes a source collection of eligibilities and returns only the ones
// that matches what is currently in the provided filters. Since filters affect
// the counts, these are also updated as a side-effect
function applyFilters(source, filters, counts) {
  return source.filter(eligibility => {
    // Only Complete
    if (filters.complete == true && eligibility.complete !== true) return false
    // Only Referred
    if (filters.referred == true && !eligibility.referredAt) return false
    // Name
    if (
      filters.name &&
      filters.name.length &&
      eligibility.profile.fullName.toLowerCase().indexOf(filters.name.toLowerCase()) == -1
    )
      return false
    // Ethnicity
    if (
      filters.ethnicities &&
      filters.ethnicities.length &&
      !filters.ethnicities.includes(eligibility.profile.ethnicity)
    )
      return false
    // Gender
    if (
      filters.genders &&
      filters.genders.length &&
      !filters.genders.includes(eligibility.profile.gender)
    )
      return false

    /* Profile Qualifications */
    if (
      (filters.grade && filters.grade.length && (filters.grade[0] > 0 || filters.grade[1] < 100)) ||
      (filters.institutions && filters.institutions.length) ||
      (filters.categories && filters.categories.length) ||
      (filters.types && filters.types.length) ||
      (filters.disciplines && filters.disciplines.length)
    ) {
      if (
        eligibility.profile.profileQualifications.filter(pq => {
          // Grade
          if (
            filters.grade &&
            filters.grade.length == 2 &&
            (parseFloat(pq.gradeNormalized) <= filters.grade[0] ||
              parseFloat(pq.gradeNormalized) > filters.grade[1])
          )
            return false
          // Institution
          if (
            filters.institutions &&
            filters.institutions.length &&
            !filters.institutions.includes(pq.institution.id)
          )
            return false
          // Category
          if (
            filters.categories &&
            filters.categories.length &&
            !filters.categories.includes(
              pq.qualification.qualificationType.qualificationCategory.id
            )
          )
            return false
          // Type
          if (
            filters.types &&
            filters.types.length &&
            !filters.types.includes(pq.qualification.qualificationType.id)
          )
            return false
          // Discipline
          if (
            filters.disciplines &&
            filters.disciplines.length &&
            new Set(filters.disciplines).intersection(new Set(pq.disciplines.map(d => d.id))).size <
              1
          )
            return false

          return true
        }).length == 0
      )
        return false
    }

    // Update counts
    if (counts[eligibility.status]) {
      counts[eligibility.status]++
    } else {
      counts[eligibility.status] = 1
    }

    // Status (after counts to update them all)
    if (filters['status'] && eligibility.status != filters['status']) return false

    return true
  })
}

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true,
}
